

import React, { useEffect, useState } from 'react';


import {
  Form, Input, InputNumber, DatePicker, Select, Button,
  Row, Col, Space, message
} from 'antd';
import dayjs from 'dayjs';
import { MedicationAPI } from '../MedicationAPI';

const { Option } = Select;

const AddMedicationForm = ({ medication, onSuccess, onCancel }) => {
  const [form] = Form.useForm();
  const [backendPrice, setBackendPrice] = useState('$0.00');
  const isUpdate = !!medication;

  useEffect(() => {
    if (isUpdate && medication) {
      const initialValues = {
        ...medication,
        expiry: medication.expiry_date ? dayjs(medication.expiry_date) : null,
      };
      form.setFieldsValue(initialValues);
      setBackendPrice(
        medication.price != null ? `$${parseFloat(medication.price).toFixed(2)}` : '$0.00'
      );
    } else {
      setBackendPrice('$0.00');
    }
  }, [isUpdate, medication, form]);

  const fetchCalculatedPrice = async () => {
    const values = form.getFieldsValue();
    const qty = values.qty;
    const pricePerStrip = values.price_per_strip;

    if (qty != null && pricePerStrip != null) {
      try {
        const tempRes = await MedicationAPI.create({
          name: 'temp',
          generic: 'temp',
          qty,
          price_per_strip: pricePerStrip,
          expiry: dayjs().format('YYYY-MM-DD'),
        });

        const tempId = tempRes.rec_Id || tempRes.Id;

        if (tempId) {
          const fullData = await MedicationAPI.getById(tempId);
          const calculatedPrice = fullData.price ?? 0;
          setBackendPrice(`$${parseFloat(calculatedPrice).toFixed(2)}`);
          await MedicationAPI.delete(tempId);
        } else {
          setBackendPrice('$0.00');
        }
      } catch {
        setBackendPrice('$0.00');
      }
    }
  };

  const handleSubmit = async (values) => {
    const payload = {
      ...values,
      expiry: values.expiry ? values.expiry.format('YYYY-MM-DD') : '',
    };

    try {
      if (isUpdate) {
        await MedicationAPI.update(medication.rec_Id, payload);
        message.success('Medication updated');
      } else {
        await MedicationAPI.create(payload);
        message.success('Medication added');
      }
      onSuccess();
    } catch {
      message.error('Failed to submit');
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onCancel();
      else if (e.ctrlKey && e.key.toLowerCase() === 'c') form.submit();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [form, onCancel]);

  return (
    
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        style={{ maxWidth: 1000, margin: '0 auto' }}
      >
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              label={<strong>Medication Name</strong>}
              name="name"
              rules={[{ required: true, message: 'Please enter medication name' }]}
            >
              <Input placeholder="e.g., Lisinopril" size="large" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={<strong>Generic</strong>}
              name="generic"
              rules={[{ required: true, message: 'Please select a generic type' }]}
            >
              <Select placeholder="Select Generic" size="large">
                <Option value="Hypertension">Hypertension</Option>
                <Option value="Diabetes">Diabetes</Option>
                <Option value="Cholesterol">Cholesterol</Option>
                <Option value="Antibiotic">Antibiotic</Option>
                <Option value="Antidepressant">Antidepressant</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              label={<strong>Expiry Date</strong>}
              name="expiry"
              rules={[{ required: true, message: 'Please select expiry date' }]}
            >
              <DatePicker picker="month" style={{ width: '100%' }} size="large" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col span={8}>
            <Form.Item
              label={<strong>Quantity</strong>}
              name="qty"
              rules={[{ required: true, message: 'Please enter quantity' }]}
            >
              <InputNumber
                min={0}
                style={{ width: '100%' }}
                placeholder="e.g., 100"
                onBlur={fetchCalculatedPrice}
                size="large"
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label={<strong>Price per Strip ($)</strong>}
              name="price_per_strip"
              rules={[{ required: true, message: 'Please enter price per strip' }]}
            >
              <InputNumber
                min={0}
                step={0.01}
                style={{ width: '100%' }}
                placeholder="e.g., 12.50"
                onBlur={fetchCalculatedPrice}
                size="large"
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label={<strong>Total Price</strong>}>
              <Input
                value={backendPrice}
                disabled
                size="large"
                style={{ backgroundColor: '#fafafa', width: '100%' }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item style={{ marginTop: 16 }}>
          <Row justify="start">
            <Space>
              <Button onClick={onCancel} size="large">Cancel</Button>
              <Button type="primary" htmlType="submit" size="large">
                {isUpdate ? 'Update' : 'Save'}
              </Button>
            </Space>
          </Row>
        </Form.Item>
      </Form>
  
  );
};

export default AddMedicationForm;
